import React from 'react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import type { FieldValidator, FormApi } from 'final-form';
import FinalFormInput from '@zola/zola-ui/src/components/Form/inputV3/FinalFormInput';
import FinalFormDatePickerV2 from '@zola/zola-ui/src/components/Form/DatePickerInputV2/FinalFormDatePickerV2';
import FinalFormDropdown from '@zola/zola-ui/src/components/Form/dropdownV3/FinalFormDropdown';
import {
  REQUIRED,
  DATE,
  DATE_WITHIN_VALID_RANGE,
  MAX_LENGTH_40,
} from '@zola/zola-ui/src/components/Form/util/validations';
import { CHECK_CURRENT_WEDDING_DATE } from 'components/common/form/commonValidations';
import type { FormValues as WeddingDetailsFormValuesTypes } from '../WeddingDetailsAccordion/mappers';
import styles from './FieldSetWebsiteWeddingDate.module.less';
import { WeddingDateDisplayFormatEnum } from '../../../../../../../util/websiteDateUtil';

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

type FieldSetWebsiteDateProps = {
  formApi: FormApi<WeddingDetailsFormValuesTypes, Partial<WeddingDetailsFormValuesTypes>>;
  formValues: WeddingDetailsFormValuesTypes;
  className?: string;
};
// react-final-form TS bug
// @ts-ignore
const WEDDING_DATE_WITHIN_RANGE: FieldValidator<string> = (
  value: string,
  allValues: WeddingDetailsFormValuesTypes
): string | undefined => {
  const isWeddingDateWithinRange = dayjs(allValues.weddingDate).isBetween(
    allValues.websiteDate.weddingDateRangeStart,
    allValues.websiteDate.weddingDateRangeEnd,
    null,
    '[]'
  );
  return isWeddingDateWithinRange ? undefined : 'Wedding date is out of range';
};

// @ts-ignore
const IS_RANGE: FieldValidator<string> = (
  value: string,
  allValues: WeddingDetailsFormValuesTypes
): string | undefined =>
  allValues.websiteDate.weddingDateRangeStart !== allValues.websiteDate.weddingDateRangeEnd
    ? undefined
    : 'Choose a range of days';

// @ts-ignore
const IS_SAME_OR_BEFORE_WEDDING_DATE: FieldValidator<string> = (
  value: string,
  allValues: WeddingDetailsFormValuesTypes
): string | undefined =>
  allValues.weddingDate && dayjs(value).isSameOrBefore(allValues.weddingDate)
    ? undefined
    : 'Must be same or before wedding date';

// @ts-ignore
const IS_AFTER_START_DATE: FieldValidator<string> = (
  value: string,
  allValues: WeddingDetailsFormValuesTypes
): string | undefined =>
  allValues.weddingDate && dayjs(value).isAfter(allValues.websiteDate.weddingDateRangeStart)
    ? undefined
    : 'Must be after start date';

const FieldSetWebsiteDate = ({
  formValues,
  formApi,
  className,
}: FieldSetWebsiteDateProps): JSX.Element => {
  const {
    weddingDate,
    weddingDateDisplayFormat,
    websiteDate: { weddingDateRangeStart },
  } = formValues;
  const { getFieldState } = formApi;
  const isWeddingDateInvalid = !formValues.weddingDate || getFieldState('weddingDate')?.invalid;

  return (
    <div className={className}>
      <div className={styles.fieldSetContainer}>
        <FinalFormDatePickerV2
          name="weddingDate"
          label="Your wedding date"
          id="wedding-info-date"
          validate={[REQUIRED, DATE, CHECK_CURRENT_WEDDING_DATE, DATE_WITHIN_VALID_RANGE]}
          isRequired
        />
        <FinalFormDropdown
          name="weddingDateDisplayFormat"
          options={[
            { label: 'Exact date', value: WeddingDateDisplayFormatEnum.EXACT },
            { label: 'Date range', value: WeddingDateDisplayFormatEnum.RANGE },
            { label: 'Custom', value: WeddingDateDisplayFormatEnum.CUSTOM },
          ]}
          isRequired
          label="Display format"
          disabled={isWeddingDateInvalid}
        />
      </div>
      {weddingDateDisplayFormat === WeddingDateDisplayFormatEnum.CUSTOM && (
        <FinalFormInput
          name="websiteDate.customDateMessage"
          label="Custom message"
          maxChars={40}
          isRequired
          validate={[REQUIRED, MAX_LENGTH_40]}
          placeholder="Stay tuned for an update!"
          tooltip="Show a short message on your website header instead of a date"
        />
      )}
      {weddingDateDisplayFormat === WeddingDateDisplayFormatEnum.RANGE && (
        <div className={styles.fieldSetContainer}>
          <FinalFormDatePickerV2
            name="websiteDate.weddingDateRangeStart"
            id="wedding-info-date-start"
            validate={[
              REQUIRED,
              DATE,
              DATE_WITHIN_VALID_RANGE,
              IS_SAME_OR_BEFORE_WEDDING_DATE,
              IS_RANGE,
              WEDDING_DATE_WITHIN_RANGE,
            ]}
            datePickerOptions={{
              month: dayjs(weddingDate).toDate(),
            }}
            isRequired
            label="Start"
          />
          <FinalFormDatePickerV2
            name="websiteDate.weddingDateRangeEnd"
            id="wedding-info-date-end"
            validate={[
              REQUIRED,
              DATE,
              DATE_WITHIN_VALID_RANGE,
              IS_AFTER_START_DATE,
              WEDDING_DATE_WITHIN_RANGE,
            ]}
            datePickerOptions={{
              disabledDays: {
                before: dayjs(weddingDateRangeStart).toDate(),
              },
              month: weddingDateRangeStart
                ? dayjs(weddingDateRangeStart).toDate()
                : dayjs(weddingDate).toDate(),
            }}
            isRequired
            label="End"
          />
        </div>
      )}
    </div>
  );
};

export default FieldSetWebsiteDate;
